import introPoses from './introPoses';

// The GalleryIntro's four hero cards, CURATED per audience rather than sampled.
//
// The automatic pick (spreadPick below, over introPoses) shows whatever four vertical
// poses spread across the sitting. Production wants specific looks in specific slots
// for some audiences, so each audience that has an opinion lists its pose numbers here
// in SLOT ORDER — top-left, bottom-left, top-right, bottom-right (see GalleryIntro).
//
// Only audiences listed here are curated. An audience with no entry (or an unknown
// school type) keeps the automatic pick — the builder passes no heroPhotos at all.
//
// The list is by POSE NUMBER, not orientation: a curated pose may be a landscape look
// (JH/HS "09", elementary "08" — both bottom-right), and it is shown as one —
// introPoses' vertical-only filter applies to the automatic pick and to the fill-ins,
// never to a curated pose. The intro measures the decoded image and sizes the tile to
// it, so a landscape look needs no per-audience layout of its own.
export const INTRO_HERO_POSES = {
  jhhs: ['01', '14', '02', '09'],
  elementary: ['01', '02', '04', '08'],
};

// school_types.id — the customer flow carries the id on customer.currentSchool
// (School#schoolTypeId), not the name.
const SCHOOL_TYPE_HIGH_SCHOOL = 1;
const SCHOOL_TYPE_MIDDLE_SCHOOL = 2;
const SCHOOL_TYPE_ELEMENTARY = 3;

// Which curated list (if any) a school gets. Middle and high school share one.
export function introAudience(school) {
  // Number(): ids arrive as strings from the JSON:API models but as numbers from
  // some callers.
  const typeId = Number(school?.schoolTypeId);
  if (typeId === SCHOOL_TYPE_HIGH_SCHOOL || typeId === SCHOOL_TYPE_MIDDLE_SCHOOL) return 'jhhs';
  if (typeId === SCHOOL_TYPE_ELEMENTARY) return 'elementary';
  return null;
}

// Pose numbers are the two-digit URL token ("01", "14") on both the pose and the
// pricelist spec; normalize so a bare "1" from anywhere still matches.
const poseNumberKey = (number) => String(number ?? '').trim().padStart(2, '0');

// Up to `count` photos spread across a sitting — first, last, and evenly between —
// rather than `count` near-duplicates from the start of the shoot. ONE implementation,
// shared by the intro's automatic pick and by the fill-in for a missing curated pose.
export function spreadPick(list, count) {
  const src = list || [];
  if (src.length <= count) return src.slice(0);
  if (count <= 1) return src.slice(0, count);
  const picked = [];
  for (let i = 0; i < count; i += 1) {
    picked.push(src[Math.round((i * (src.length - 1)) / (count - 1))]);
  }
  return picked;
}

const hasImage = (pose) => !!(pose?.imageUrl || pose?.watermarkUrl);

/**
 * The curated hero poses for this school's audience, in slot order.
 *
 * Returns null when the audience has no curated list, so the caller can fall back to
 * the automatic pick. A curated pose the student wasn't shot in is filled from the
 * automatic spread (vertical poses from the pricelist's choices, minus any already
 * placed) so the slot is never blank — the other curated poses keep their slots.
 */
export default function introHeroPoses(poses, packagePoses, school) {
  const list = INTRO_HERO_POSES[introAudience(school)];
  if (!list) return null;

  const all = (poses || []).filter(hasImage);
  const curated = list.map((number) => (
    all.find((pose) => poseNumberKey(pose.number) === poseNumberKey(number)) || null
  ));

  // Fill-ins must not repeat a LOOK already on screen: a sitting can carry the same pose
  // number twice (two cameras), and the other camera's "01" is a near-duplicate of the
  // "01" already placed — so exclude by pose number, not just by row.
  const placedNumbers = new Set(curated.filter(Boolean).map((pose) => poseNumberKey(pose.number)));
  const fillers = spreadPick(
    introPoses(all, packagePoses).filter((pose) => !placedNumbers.has(poseNumberKey(pose.number))),
    curated.filter((pose) => !pose).length,
  );

  return curated.map((pose) => pose || fillers.shift() || null).filter(Boolean);
}
